import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getActiveTenantId } from 'app/core/utils/helpers'
import withProgress from 'core/components/progress/withProgress'
import Dropdown from 'core/elements/dropdown'
import GlobalDropdownWithSearch from 'core/elements/dropdown/GlobalDropdownWithSearch'
import useListAction from 'core/hooks/useListAction'
import { canManageTenantsAndUsers } from 'core/utils/rbac'
import { listTenants, listUserTenants } from './new-actions'
import { tenantsSelector, userTenantsSelector } from './selectors'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

interface TenantPicklistProps {
  disabled?: boolean
  name?: string
  label?: string
  includeNoneOption?: boolean
  includeAllOption?: boolean
  excludeItems?: string[]
  compact?: boolean
  onChange: (value: string) => void
  value: string
  width?: number
  align?: 'left' | 'right'
  [key: string]: any
}

export default function TenantPicklist({
  disabled = false,
  name = 'tenant',
  label = 'Tenant',
  includeNoneOption = false,
  includeAllOption = false,
  excludeItems = [],
  compact = true,
  onChange,
  value,
  width = 180,
  align = 'left',
  labelPosition = 'top',
  ...rest
}: TenantPicklistProps) {
  const activeTenantId = getActiveTenantId()
  // System users & legacy admin should see all projects in the domain
  // Other users should see the projects they have access to
  const { loading } = useListAction(listUserTenants, { preRequisites: !canManageTenantsAndUsers() })
  const tenants = useSelector(userTenantsSelector)
  const { loading: loadingDomainTenants } = useListAction(listTenants, {
    preRequisites: canManageTenantsAndUsers(),
  })
  const domainTenants = useSelector(tenantsSelector)
  const tenantsList = canManageTenantsAndUsers() ? domainTenants : tenants

  const items = useMemo(() => {
    // No cases yet where we need to show tenants from other domains
    // so filter them out here
    const currentDomainId = tenantsList.find((tenant) => tenant.id === activeTenantId)?.domain_id
    const tenantOptions = tenantsList
      .filter(
        (tenant) => !excludeItems?.includes(tenant.id) && currentDomainId === tenant?.domain_id,
      )
      .map((tenant) => ({
        label: tenant.name,
        value: tenant.id,
      }))

    const result = []
    if (includeAllOption) {
      result.push({ label: 'All Tenants', value: '' })
    }
    if (includeNoneOption) {
      result.push({ label: '-- None --', value: '' })
    }
    return [...result, ...tenantOptions]
  }, [tenantsList, includeNoneOption, includeAllOption, excludeItems])

  return compact ? (
    <AsyncDropdown
      {...rest}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loading || loadingDomainTenants}
      items={items}
    />
  ) : (
    <GlobalDropdownWithSearch
      label={label}
      items={items}
      loading={loading || loadingDomainTenants}
      onChange={(item) => onChange(item.value)}
      value={value}
      {...(labelPosition === 'left' ? { showLabelOnLeft: true } : { showLabelOnTop: true })}
      align={align}
      disabled={disabled}
      width={width}
    />
  )
}
